import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Filter, Loader2, Eye, Search, ChevronLeft, ChevronRight, X, Upload, Download } from 'lucide-react';
import { toast } from 'sonner';
import { getCategoryLabel } from '../lib/constants';
import { formatFactorValue } from '../lib/format';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

// Multi-select filter component with search (mirrors EmissionFactorsPage)
const MultiSelectFilter = ({ label, options, selected, onSelectionChange, testIdPrefix = "" }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;
    const s = searchTerm.toLowerCase();
    return options.filter(o => o.label.toLowerCase().includes(s) || o.value.toLowerCase().includes(s));
  }, [options, searchTerm]);

  useEffect(() => {
    if (!open) setSearchTerm('');
  }, [open]);

  const toggleOption = (value) => {
    if (selected.includes(value)) {
      onSelectionChange(selected.filter(v => v !== value));
    } else {
      onSelectionChange([...selected, value]);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-8 border-dashed"
          data-testid={`${testIdPrefix}filter-trigger`}
        >
          <Filter className="mr-2 h-3 w-3" />
          {label}
          {selected.length > 0 && (
            <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">
              {selected.length}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0" align="start">
        <div className="p-2 border-b">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{label}</span>
            <div className="flex gap-1">
              {filteredOptions.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => onSelectionChange(filteredOptions.map(o => o.value))}
                >
                  All
                </Button>
              )}
              {selected.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={() => onSelectionChange([])}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 pl-7 text-sm"
            />
          </div>
        </div>
        <div className="max-h-[300px] overflow-auto p-2">
          {filteredOptions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2 text-center">
              {options.length === 0 ? 'No options' : 'No matches found'}
            </p>
          ) : (
            filteredOptions.map((option) => (
              <div
                key={option.value}
                className="flex items-center space-x-2 py-1 px-1 rounded hover:bg-gray-100 cursor-pointer"
                onClick={() => toggleOption(option.value)}
                data-testid={`${testIdPrefix}option-${option.value}`}
              >
                <Checkbox checked={selected.includes(option.value)} className="pointer-events-none" />
                <span className="text-sm truncate flex-1" title={option.label}>
                  {option.label}
                </span>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default function CustomMappingsPage() {
  const [mappings, setMappings] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [exporting, setExporting] = useState(false);
  const { canEdit } = useAuth();
  const navigate = useNavigate();

  // Downloads every approved mapping, ordered by ID
  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/custom-mappings/export/excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'custom_mappings.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export custom mappings');
    } finally {
      setExporting(false);
    }
  };

  // Multi-select column filters
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [subCategoryFilter, setSubCategoryFilter] = useState([]);
  const [typeFilter, setTypeFilter] = useState([]);
  const [subTypeFilter, setSubTypeFilter] = useState([]);
  const [efCategoryFilter, setEfCategoryFilter] = useState([]);

  // Distinct filter dropdown values, loaded once from the server
  const [filterOptionsData, setFilterOptionsData] = useState({
    categories: [],
    sub_categories: [],
    types: [],
    sub_types: [],
    ef_categories: [],
  });

  const fetchMappings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (debouncedSearch) params.append('search', debouncedSearch);
      categoryFilter.forEach(v => params.append('category', v));
      subCategoryFilter.forEach(v => params.append('sub_category', v));
      typeFilter.forEach(v => params.append('type', v));
      subTypeFilter.forEach(v => params.append('sub_type', v));
      efCategoryFilter.forEach(v => params.append('ef_category', v));
      params.append('page', String(currentPage));
      params.append('page_size', String(pageSize));

      const response = await axios.get(`${API_URL}/custom-mappings?${params}`);
      setMappings(response.data.items || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 0);
    } catch (error) {
      toast.error('Failed to load custom mappings');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearch, categoryFilter, subCategoryFilter, typeFilter,
      subTypeFilter, efCategoryFilter, currentPage, pageSize]);

  // Debounce the search box so we don't hit the API on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  // Load distinct filter dropdown values once on mount
  useEffect(() => {
    axios.get(`${API_URL}/custom-mappings/filter-options`)
      .then(res => setFilterOptionsData(res.data))
      .catch(() => { /* dropdowns just stay empty if this fails */ });
  }, []);

  // Build filter options from the distinct values loaded from the server
  const filterOptions = useMemo(() => {
    const o = filterOptionsData;
    return {
      categories: (o.categories || []).map(c => ({ value: c, label: getCategoryLabel(c) })),
      subCategories: (o.sub_categories || []).map(s => ({ value: s, label: s })),
      types: (o.types || []).map(t => ({ value: t, label: t })),
      subTypes: (o.sub_types || []).map(s => ({ value: s, label: s })),
      efCategories: (o.ef_categories || []).map(c => ({ value: c, label: getCategoryLabel(c) })),
    };
  }, [filterOptionsData]);

  const hasActiveFilters =
    categoryFilter.length > 0 ||
    subCategoryFilter.length > 0 ||
    typeFilter.length > 0 ||
    subTypeFilter.length > 0 ||
    efCategoryFilter.length > 0;

  const clearAllFilters = () => {
    setCategoryFilter([]);
    setSubCategoryFilter([]);
    setTypeFilter([]);
    setSubTypeFilter([]);
    setEfCategoryFilter([]);
  };

  // Filtering, search and pagination are all handled server-side. `mappings`
  // already holds just the current page for the active filters.
  const paginatedMappings = mappings;

  // Reset to page 1 when the active query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, statusFilter, pageSize, categoryFilter, subCategoryFilter, typeFilter, subTypeFilter, efCategoryFilter]);

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="custom-mappings-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground">Custom Mappings</h1>
            <p className="text-muted-foreground mt-1">Map custom categories to emission factors</p>
          </div>
          {canEdit() && (
            <div className="flex gap-2">
              <Button variant="outline" className="gap-2" onClick={handleExport} disabled={exporting} data-testid="export-mappings-btn">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Export Excel
              </Button>
              <Link to="/custom-mappings/bulk">
                <Button variant="outline" className="gap-2" data-testid="bulk-mapping-btn">
                  <Upload className="w-4 h-4" />
                  Bulk Add
                </Button>
              </Link>
              <Link to="/custom-mappings/new">
                <Button className="btn-primary gap-2" data-testid="create-mapping-btn">
                  <Plus className="w-4 h-4" />
                  New Mapping
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Filters */}
        <Card className="border-gray-200">
          <CardContent className="p-4">
            <div className="flex flex-col gap-4">
              {/* Search + Status */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by ID, category, sub category, type..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10 input-eco"
                    data-testid="search-input"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-48 input-eco" data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Column filters */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-muted-foreground mr-1">Filters:</span>
                <MultiSelectFilter
                  label="Custom Category"
                  options={filterOptions.categories}
                  selected={categoryFilter}
                  onSelectionChange={setCategoryFilter}
                  testIdPrefix="cm-category-"
                />
                <MultiSelectFilter
                  label="Custom Sub Category"
                  options={filterOptions.subCategories}
                  selected={subCategoryFilter}
                  onSelectionChange={setSubCategoryFilter}
                  testIdPrefix="cm-sub-category-"
                />
                <MultiSelectFilter
                  label="Custom Type"
                  options={filterOptions.types}
                  selected={typeFilter}
                  onSelectionChange={setTypeFilter}
                  testIdPrefix="cm-type-"
                />
                <MultiSelectFilter
                  label="Custom Sub Type"
                  options={filterOptions.subTypes}
                  selected={subTypeFilter}
                  onSelectionChange={setSubTypeFilter}
                  testIdPrefix="cm-sub-type-"
                />
                <MultiSelectFilter
                  label="EF Category"
                  options={filterOptions.efCategories}
                  selected={efCategoryFilter}
                  onSelectionChange={setEfCategoryFilter}
                  testIdPrefix="cm-ef-category-"
                />
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" className="h-8 px-2" onClick={clearAllFilters} data-testid="clear-filters-btn">
                    <X className="w-3 h-3 mr-1" />
                    Clear All
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="border-gray-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : total === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No custom mappings found</p>
                {canEdit() && (
                  <Link to="/custom-mappings/new">
                    <Button className="mt-4 btn-primary gap-2">
                      <Plus className="w-4 h-4" />
                      Create your first mapping
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="table-dense">ID</TableHead>
                        <TableHead className="table-dense">Custom Category</TableHead>
                        <TableHead className="table-dense">Custom Sub Category</TableHead>
                        <TableHead className="table-dense">Custom Type</TableHead>
                        <TableHead className="table-dense">Custom Sub Type</TableHead>
                        <TableHead className="table-dense">Additional Information</TableHead>
                        <TableHead className="table-dense">Linked EF ID</TableHead>
                        <TableHead className="table-dense">EF Category</TableHead>
                        <TableHead className="table-dense">EF CO2e</TableHead>
                        <TableHead className="table-dense">Status</TableHead>
                        <TableHead className="table-dense">Created By</TableHead>
                        <TableHead className="table-dense text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paginatedMappings.map((mapping) => (
                        <TableRow key={mapping.id} className="table-dense" data-testid={`mapping-row-${mapping.id}`}>
                          <TableCell className="font-mono text-sm">{mapping.ef_training_set_id || mapping.id}</TableCell>
                          <TableCell className="font-medium">{getCategoryLabel(mapping.category)}</TableCell>
                          <TableCell>{mapping.sub_category}</TableCell>
                          <TableCell>{mapping.type || '—'}</TableCell>
                          <TableCell>{mapping.sub_type || '—'}</TableCell>
                          <TableCell className="max-w-[300px] truncate" title={mapping.additional_information || ''}>{mapping.additional_information || '—'}</TableCell>
                          <TableCell className="font-mono text-sm">
                            <span className="text-primary">#{mapping.desired_emission_factor_id}</span>
                          </TableCell>
                          <TableCell>{getCategoryLabel(mapping.ef_category)}</TableCell>
                          <TableCell className="font-mono text-sm">{formatFactorValue(mapping.ef_co2e)}</TableCell>
                          <TableCell><StatusBadge status={mapping.status} /></TableCell>
                          <TableCell className="text-sm">{mapping.created_by}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/custom-mappings/${mapping.id}`)}
                              title="View Details"
                              data-testid={`view-mapping-${mapping.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className="text-sm text-muted-foreground">
                        Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, total)} of {total} results
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Rows per page:</span>
                        <Select value={String(pageSize)} onValueChange={(val) => setPageSize(Number(val))}>
                          <SelectTrigger className="w-[70px] h-8" data-testid="page-size-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PAGE_SIZE_OPTIONS.map(size => (
                              <SelectItem key={size} value={String(size)}>{size}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        data-testid="prev-page-btn"
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Previous
                      </Button>
                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? "default" : "outline"}
                              size="sm"
                              className="w-8 h-8 p-0"
                              onClick={() => setCurrentPage(pageNum)}
                              data-testid={`page-${pageNum}-btn`}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        data-testid="next-page-btn"
                      >
                        Next
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
